import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Row, Col, Card, Typography, Tag, Spin, Button, Avatar } from "antd";
import {
  BookOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
  FireFilled,
  CheckCircleFilled,
  ArrowRightOutlined,
  ClockCircleOutlined,
  ExclamationCircleFilled,
  StarFilled,
} from "@ant-design/icons";
import { api } from "@/services/api";
import {
  API_ENDPOINTS,
  ASSIGNMENT_STATUS_COLORS,
  AVATAR_COLORS,
} from "@/constants";
import { useAuthStore } from "@/stores/authStore";
import { useHasRealTeachers } from "@/hooks/useHasRealTeachers";
import { daysUntil, isOverdue } from "@/utils/date";
import type { ApiResponse, DeckAssignment, StudentAnalytics } from "@/types";

const { Title, Text } = Typography;

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function getAvatarColor(name: string | undefined): string {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function DeadlineChip({ deadline }: { deadline: string | null | undefined }) {
  if (!deadline) return null;
  const overdue = isOverdue(deadline);
  const days = daysUntil(deadline);
  if (overdue) {
    return (
      <Text style={{ color: "#ef4444", fontSize: 12, fontWeight: 500 }}>
        <ExclamationCircleFilled style={{ marginRight: 3 }} />
        Overdue
      </Text>
    );
  }
  if (days === null) return null;
  const color = days <= 2 ? "#f59e0b" : "#64748b";
  const label =
    days === 0 ? "Due today" : days === 1 ? "Due tomorrow" : `${days}d left`;
  return (
    <Text style={{ color, fontSize: 12 }}>
      <ClockCircleOutlined style={{ marginRight: 3 }} />
      {label}
    </Text>
  );
}

const NAV_CARDS = [
  {
    key: "decks",
    path: "/student/decks",
    label: "My Decks",
    description: "Review your assigned card decks",
    icon: <BookOutlined />,
    iconBg: "#f0f5ff",
    iconColor: "#3b82f6",
    accentColor: "#3b82f6",
  },
  {
    key: "teachers",
    path: "/student/teachers",
    label: "My Teachers",
    description: "Connect with your teachers",
    icon: <TeamOutlined />,
    iconBg: "#fdf4ff",
    iconColor: "#a855f7",
    accentColor: "#a855f7",
  },
  {
    key: "progress",
    path: "/student/progress",
    label: "My Progress",
    description: "Track your learning journey",
    icon: <TrophyOutlined />,
    iconBg: "#fffbeb",
    iconColor: "#f59e0b",
    accentColor: "#f59e0b",
  },
  {
    key: "profile",
    path: "/student/profile",
    label: "My Profile",
    description: "Manage your account settings",
    icon: <UserOutlined />,
    iconBg: "#f0fdf4",
    iconColor: "#16a34a",
    accentColor: "#16a34a",
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 480);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 480);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const { data: decksData, isLoading: decksLoading } = useQuery({
    queryKey: ["student", "decks"],
    queryFn: () =>
      api.get<
        never,
        ApiResponse<{
          data: DeckAssignment[];
          meta: { cursor?: string; hasMore: boolean };
        }>
      >(API_ENDPOINTS.STUDENT_DECKS),
  });

  const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
    queryKey: ["student", "analytics", user?.id],
    queryFn: () =>
      api.get<never, ApiResponse<StudentAnalytics>>(
        `${API_ENDPOINTS.ANALYTICS_STUDENTS}/${user?.id}`,
      ),
    enabled: !!user?.id,
  });

  const hasRealTeachers = useHasRealTeachers();

  const assignments: DeckAssignment[] = decksData?.data?.data ?? [];
  const analytics = analyticsData?.data;

  // Show up to 3 pending/in-progress decks as "continue learning"
  const activeDecks = assignments
    .filter((a) => a.status === "PENDING" || a.status === "IN_PROGRESS")
    .slice(0, 3);

  const totalAssigned = assignments.length;
  const completed = assignments.filter((a) => a.status === "COMPLETED").length;
  const pending = assignments.filter(
    (a) => a.status === "PENDING" || a.status === "IN_PROGRESS",
  ).length;

  const avatarColor = getAvatarColor(user?.name);
  const isLoading = decksLoading || analyticsLoading;

  if (isLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* ── Welcome Banner ── */}
      <Card
        style={{
          borderRadius: 16,
          marginBottom: 24,
          background: "linear-gradient(135deg, #1a1d2e 0%, #2d3561 100%)",
          border: "none",
          overflow: "hidden",
        }}
        styles={{ body: { padding: isMobile ? "18px 16px" : "28px 28px" } }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: isMobile ? 12 : 20 }}>
          <Avatar
            size={isMobile ? 48 : 64}
            style={{
              background: avatarColor,
              fontWeight: 700,
              fontSize: isMobile ? 22 : 28,
              flexShrink: 0,
              border: "3px solid rgba(255,255,255,0.2)",
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase() ?? "S"}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <Text style={{ color: "rgba(255,255,255,0.65)", fontSize: isMobile ? 12 : 14, display: "block" }}>
                  {getGreeting()}
                </Text>
                <Title
                  level={2}
                  style={{
                    color: "#fff",
                    margin: "2px 0 6px",
                    fontSize: isMobile ? 20 : 26,
                    fontFamily: "'Nunito', 'Segoe UI', sans-serif",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user?.name ?? "Student"}!
                </Title>
              </div>
              {analytics?.averageComprehensionRate != null && (
                <div
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: 10,
                    padding: isMobile ? "8px 10px" : "12px 18px",
                    textAlign: "center",
                    flexShrink: 0,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 3, justifyContent: "center", marginBottom: 2 }}>
                    <StarFilled style={{ color: "#fbbf24", fontSize: isMobile ? 11 : 14 }} />
                    <Text style={{ color: "#fbbf24", fontSize: isMobile ? 16 : 22, fontWeight: 700 }}>
                      {Math.round(analytics.averageComprehensionRate * 100)}%
                    </Text>
                  </div>
                  <Text style={{ color: "rgba(255,255,255,0.55)", fontSize: 11 }}>
                    Comprehension
                  </Text>
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: isMobile ? 10 : 16, flexWrap: "wrap" }}>
              {analytics?.currentStreak != null && analytics.currentStreak > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <FireFilled style={{ color: "#f59e0b", fontSize: isMobile ? 13 : 16 }} />
                  <Text style={{ color: "#fde68a", fontSize: isMobile ? 12 : 14, fontWeight: 600 }}>
                    {analytics.currentStreak} day streak
                  </Text>
                </div>
              )}
              {completed > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <CheckCircleFilled style={{ color: "#34d399", fontSize: isMobile ? 12 : 15 }} />
                  <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: isMobile ? 12 : 14 }}>
                    {completed} deck{completed !== 1 ? "s" : ""} completed
                  </Text>
                </div>
              )}
              {pending > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <BookOutlined style={{ color: "#93c5fd", fontSize: isMobile ? 12 : 15 }} />
                  <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: isMobile ? 12 : 14 }}>
                    {pending} deck{pending !== 1 ? "s" : ""} to go
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* ── Quick Stats ── */}
      {analytics && (
        <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
          {[
            {
              label: "Cards Reviewed",
              value: analytics.totalCardsReviewed,
              color: "#6366f1",
              path: "/student/progress",
            },
            {
              label: "Decks Assigned",
              value: totalAssigned,
              color: "#0891b2",
              path: "/student/decks",
            },
            {
              label: "Completed",
              value: completed,
              color: "#16a34a",
              path: "/student/decks?filter=COMPLETED",
            },
            {
              label: "Day Streak",
              value: analytics.currentStreak,
              color: "#f59e0b",
              path: "/student/progress",
            },
          ].map(({ label, value, color, path }) => (
            <Col xs={12} sm={6} key={label}>
              <Card
                hoverable
                onClick={() => navigate(path)}
                style={{
                  borderRadius: 12,
                  textAlign: "center",
                  cursor: "pointer",
                }}
                styles={{ body: { padding: isMobile ? "12px 8px" : "16px 12px" } }}
              >
                <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color }}>
                  {value ?? 0}
                </div>
                <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
                  {label}
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* ── Continue Learning ── */}
      {activeDecks.length > 0 && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Text strong style={{ fontSize: 16, color: "#1e293b" }}>
              Continue Learning
            </Text>
            <Button
              type="link"
              size="small"
              onClick={() => navigate("/student/decks")}
              style={{ padding: 0, fontSize: 13 }}
            >
              View all
            </Button>
          </div>
          <Row gutter={[12, 12]}>
            {activeDecks.map((assignment) => {
              const deck = assignment.deck;
              const statusStyle = ASSIGNMENT_STATUS_COLORS[assignment.status];
              const deadline = assignment.deadline ?? deck?.deadline;
              const isInProgress = assignment.status === "IN_PROGRESS";

              return (
                <Col xs={24} sm={12} lg={8} key={assignment.id}>
                  <Card
                    hoverable
                    onClick={() =>
                      navigate(`/student/decks/${assignment.deckId}/review`)
                    }
                    style={{
                      borderRadius: 12,
                      cursor: "pointer",
                      borderLeft: `4px solid ${isInProgress ? "#3b82f6" : "#e2e8f0"}`,
                    }}
                    styles={{ body: { padding: "14px 16px" } }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <BookOutlined
                          style={{
                            color: "#3b82f6",
                            fontSize: 15,
                            flexShrink: 0,
                          }}
                        />
                        <Text
                          strong
                          style={{
                            fontSize: 14,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                        >
                          {deck?.topicName ?? "Untitled Deck"}
                        </Text>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <Tag
                            style={{
                              margin: 0,
                              fontSize: 11,
                              color: statusStyle?.color ?? "#64748b",
                              background: statusStyle?.bg ?? "#f1f5f9",
                              border: "none",
                              fontWeight: 500,
                            }}
                          >
                            {assignment.status.replace("_", " ")}
                          </Tag>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {deck?.cardCount ?? 0} cards
                          </Text>
                        </div>
                        <DeadlineChip deadline={deadline} />
                      </div>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {/* ── Nav Cards ── */}
      <div style={{ marginTop: activeDecks.length > 0 ? 24 : 0 }}>
        <Row gutter={[12, 12]}>
          {NAV_CARDS.filter((item) => item.key !== "teachers" || hasRealTeachers).map((item) => (
            <Col xs={12} sm={12} lg={6} key={item.key}>
              <Card
                hoverable
                onClick={() => navigate(item.path)}
                style={{ borderRadius: 12, cursor: "pointer" }}
                styles={{ body: { padding: isMobile ? "12px" : "16px" } }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 8 : 10 }}>
                  <div
                    style={{
                      width: isMobile ? 34 : 40,
                      height: isMobile ? 34 : 40,
                      borderRadius: 10,
                      background: item.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: isMobile ? 15 : 18,
                      color: item.iconColor,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <Text strong style={{ fontSize: isMobile ? 13 : 14, display: "block" }}>
                      {item.label}
                    </Text>
                    {!isMobile && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {item.description}
                      </Text>
                    )}
                  </div>
                  <ArrowRightOutlined style={{ color: item.accentColor, fontSize: 12 }} />
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* ── Empty state when no decks at all ── */}
      {assignments.length === 0 && (
        <Card
          style={{
            borderRadius: 14,
            textAlign: "center",
            background: "#fafafa",
            border: "2px dashed #e2e8f0",
            marginTop: 24,
          }}
          styles={{ body: { padding: "40px 24px" } }}
        >
          <BookOutlined
            style={{ fontSize: 40, color: "#cbd5e1", marginBottom: 12 }}
          />
          <Text
            style={{
              fontSize: 16,
              display: "block",
              marginBottom: 6,
              color: "#475569",
            }}
          >
            No decks assigned yet
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: 14, display: "block", marginBottom: 16 }}
          >
            {hasRealTeachers
              ? "Ask your teacher for an invite code and join them to get started!"
              : "New decks will appear here soon!"}
          </Text>
          <Button type="primary" onClick={() => navigate(hasRealTeachers ? "/student/teachers" : "/student/decks")}>
            {hasRealTeachers ? "Connect with a Teacher" : "Browse Decks"}
          </Button>
        </Card>
      )}
    </div>
  );
}
